import type { ActionTree } from "vuex";
import {
  LOAD_DASHBOARDS,
  LOAD_DASHBOARDS_SUCCESS,
  ADD_DASHBOARD,
  ADD_DASHBOARD_SUCCESS,
  ADD_GRAPH,
  ADD_GRAPH_SUCCESS,
  LOAD_DASHBOARDS_FAILURE,
  ADD_DASHBOARD_FAILURE,
  ADD_GRAPH_FAILURE,
  LOAD_GRAPH,
  LOAD_GRAPH_SUCCESS,
  LOAD_GRAPH_FAILURE,
  UPDATE_DASHBOARD,
  UPDATE_DASHBOARD_SUCCESS,
  UPDATE_DASHBOARD_FAILURE,
  UPDATE_GRAPH,
  UPDATE_GRAPH_SUCCESS,
  UPDATE_GRAPH_FAILURE,
  UPDATE_GRAPH_IN_DASH,
  UPDATE_GRAPH_IN_DASH_SUCCESS,
  UPDATE_GRAPH_IN_DASH_FAILURE,
  DELETE_DASHBOARD,
  DELETE_DASHBOARD_SUCCESS,
  DELETE_DASHBOARD_FAILURE,
  UPDATE_GRAPH_DATA,
  UPDATE_GRAPH_DATA_FAILURE,
  UPDATE_GRAPH_DATA_SUCCESS,
  LOAD_DASHBOARD,
  LOAD_DASHBOARD_SUCCESS,
  LOAD_DASHBOARD_FAILURE,
  UPDATE_DASHBOARDS,
  ADD_REPORT_GRAPH,
  ADD_REPORT_GRAPH_SUCCESS,
  ADD_REPORT_GRAPH_FAILURE,
  LOAD_DASHBOARD_REPORT,
  LOAD_DASHBOARD_REPORT_SUCCESS,
  LOAD_DASHBOARD_REPORT_FAILURE,
} from "./mutations";
import { httpClient, type AxiosResponse } from "@/services/httpClient";
import type {
  RootState,
  DataExplorerState,
  Dashboard,
  Graph,
  GraphData,
} from "@/types";
import router from "@/router";
import {
  HOSTS,
  ENDPOINTS,
  TABLEAU_TYPE,
  BAR_PLOT_TYPE,
  BULLET,
  GAUGE,
} from "@/constants";

export const actions: ActionTree<DataExplorerState, RootState> = {
  // DASH
  fetchDashs({ commit }, params: any) {
    commit(LOAD_DASHBOARDS);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}`, {
        params: { ...params },
      })
      .then(
        (response: AxiosResponse<Dashboard[]>) => {
          commit(LOAD_DASHBOARDS_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_DASHBOARDS_FAILURE, error.message);
        },
      );
  },
  fetchDashboardById({ commit }, params) {
    commit(LOAD_DASHBOARD);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}/${params.dashboardId}`)
      .then(
        (response: AxiosResponse<Dashboard>) => {
          commit(LOAD_DASHBOARD_SUCCESS, response.data);
          this.dispatch("dataExplorer/selectDash", {
            ...response.data,
            periode: params.periode,
          });
        },
        (error) => {
          commit(LOAD_DASHBOARD_FAILURE, error.message);
        },
      );
  },

  fetchDashboardByIdReport({ commit }, dashboardId) {
    commit(LOAD_DASHBOARD_REPORT);
    if (dashboardId && dashboardId.length) {
      httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}/${dashboardId}`).then(
        (response: AxiosResponse<Dashboard>) => {
          commit(LOAD_DASHBOARD_REPORT_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD_DASHBOARD_REPORT_FAILURE, error.message);
        },
      );
    } else {
      commit(LOAD_DASHBOARD_REPORT_SUCCESS, null);
    }
  },

  addDash({ commit }, dash: Dashboard) {
    commit(ADD_DASHBOARD);
    httpClient.post(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}`, dash).then(
      (response: AxiosResponse<Dashboard>) => {
        commit(ADD_DASHBOARD_SUCCESS, response.data);
        router.push({
          path: `/data-explorer/dash/${response.data.id}`,
        });
      },
      (error) => {
        commit(ADD_DASHBOARD_FAILURE, error.message);
      },
    );
  },

  duplicateDash({ commit }, dash: Dashboard) {
    commit(ADD_DASHBOARD);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}/${dash.id}`, {
        name: dash.name,
      })
      .then(
        (response: AxiosResponse<Dashboard>) => {
          commit(ADD_DASHBOARD_SUCCESS, response.data);
          router.push({
            path: `/data-explorer/dash/${response.data.id}`,
          });
        },
        (error) => {
          commit(ADD_DASHBOARD_FAILURE, error.message);
        },
      );
  },
  updateDashs({ commit }, dash: any) {
    commit(UPDATE_DASHBOARDS, dash);
  },
  updateDash({ commit }, dash: any) {
    commit(UPDATE_DASHBOARD);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}/${dash.id}`, {
        ...dash,
        id: undefined,
        graphs: [
          ...dash.graphs.map((graph: any) => ({
            id: graph.id,
            row: graph.row,
            dashboardFrontConfig: graph.dashboardFrontConfig,
          })),
        ],
      })
      .then(
        (response: any) => {
          this.dispatch("dataExplorer/updateDashs", response.data);
          commit(UPDATE_DASHBOARD_SUCCESS, {
            ...response.data,
            graphs: [...dash.graphs],
          });
        },
        (error) => {
          commit(UPDATE_DASHBOARD_FAILURE, error.message);
        },
      );
  },

  deleteDash({ commit }, dashId: string) {
    commit(DELETE_DASHBOARD);
    httpClient.delete(`${HOSTS.AWS}/${ENDPOINTS.DASHBOARD}/${dashId}`).then(
      () => {
        commit(DELETE_DASHBOARD_SUCCESS, dashId);
        router.push({
          path: `/data-explorer`,
        });
      },
      (error) => {
        commit(DELETE_DASHBOARD_FAILURE, error.message);
      },
    );
  },

  async selectDash({ commit }, dash: Dashboard) {
    commit(UPDATE_GRAPH_IN_DASH);
    if (dash.graphs) {
      Promise.all(
        dash.graphs.map((graph: any) => {
          const haveTab =
            graph.frontConfig.type === TABLEAU_TYPE ||
            graph.frontConfig.type === BAR_PLOT_TYPE ||
            graph.frontConfig.type === BULLET ||
            graph.frontConfig.type === GAUGE;

          const haveSampling = graph.frontConfig.graphSampling;
          const sampling = graph.frontConfig.sampling;
          const samplingUnit = graph.frontConfig.samplingUnit;
          const periode = dash.periode;
          return httpClient.get(
            `${HOSTS.AWS}/${
              haveTab ? ENDPOINTS.ALLTABLEDATA : ENDPOINTS.ALLGRAPHDATA
            }`,
            {
              params: {
                graphId: graph.id,
                start: periode ? periode[0] : "",
                end: periode ? periode[1] : "",
                sampling: !haveTab && haveSampling ? sampling : undefined,
                samplingUnit:
                  !haveTab && haveSampling ? samplingUnit : undefined,
                timeserie: !haveTab ? true : undefined,
                numberOfPoints: !haveTab ? 1000 : undefined,
              },
            },
          );
        }),
      ).then(
        (response: any) => {
          dash.graphs.forEach((graph: any, graphIndex: number) => {
            const haveTab =
              graph.frontConfig.type === TABLEAU_TYPE ||
              graph.frontConfig.type === BAR_PLOT_TYPE ||
              graph.frontConfig.type === BULLET ||
              graph.frontConfig.type === GAUGE;
            commit(
              UPDATE_GRAPH_IN_DASH_SUCCESS,
              haveTab
                ? {
                    ...graph,
                    dashboardId: dash.id,
                    graphVariables: graph.graphVariables
                      ? graph.graphVariables.map(
                          (variable: any, index: number) => ({
                            ...variable,
                            data: response[graphIndex].data.objects[
                              index
                            ].data.map((elm: any) => {
                              return {
                                ...elm,
                                type: elm.period,
                                period: undefined,
                              };
                            }),
                          }),
                        )
                      : [],
                  }
                : {
                    ...graph,
                    dashboardId: dash.id,
                    sampling: response[graphIndex].data
                      ? response[graphIndex].data.sampling
                      : 1,
                    samplingUnit: response[graphIndex].data
                      ? response[graphIndex].data.samplingUnit
                      : "hours",
                    graphVariables: graph.graphVariables
                      ? graph.graphVariables.map((variable: any) => {
                          const varIndex = response[
                            graphIndex
                          ].data.variables.findIndex(
                            (varResponse: any) =>
                              varResponse.id === variable.object.id &&
                              varResponse.aggregation.id ===
                                variable.aggregation.id,
                          );
                          return {
                            ...variable,
                            autoSampling:
                              response[graphIndex].data.variables[varIndex]
                                .autoSampling,
                            data: response[graphIndex].data.variables[varIndex]
                              .data,
                            sampling:
                              response[graphIndex].data.variables[varIndex]
                                .sampling,
                            samplingUnit:
                              response[graphIndex].data.variables[varIndex]
                                .samplingUnit,
                          };
                        })
                      : [],
                  },
            );
          });
        },
        (error) => {
          commit(UPDATE_GRAPH_IN_DASH_FAILURE, error.message);
        },
      );
    }
  },

  // GRAPH
  addGraph({ commit }, params: any) {
    commit(ADD_GRAPH);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.GRAPH}`, {
        ...params.graph,
      })
      .then(
        (response: AxiosResponse<Graph>) => {
          commit(ADD_GRAPH_SUCCESS);
          router.push({
            path: `/data-explorer/graph/${params.dash.id}/${response.data.id}`,
          });
        },
        (error) => {
          commit(ADD_GRAPH_FAILURE, error.message);
        },
      );
  },

  loadGraph({ commit, dispatch }, params: any) {
    commit(LOAD_GRAPH);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.GRAPH}/${params.graphId}`).then(
      (response: AxiosResponse<Graph>) => {
        commit(LOAD_GRAPH_SUCCESS, {
          ...response.data,
          periode: params.periode,
          sampling: response.data.frontConfig.sampling || 1,
          samplingUnit: response.data.frontConfig.samplingUnit || "hours",
        });
        dispatch(
          "dataExplorer/loadAllGraphData",
          {
            graph: {
              ...response.data,
            },
            haveTab:
              response.data.frontConfig.type === TABLEAU_TYPE ||
              response.data.frontConfig.type === BAR_PLOT_TYPE ||
              response.data.frontConfig.type === BULLET ||
              response.data.frontConfig.type === GAUGE,
            periode: params.periode,
          },
          {
            root: true,
          },
        );
      },
      (error) => {
        commit(LOAD_GRAPH_FAILURE, error.message);
      },
    );
  },
  updateGraphInStore({ commit }, graph: Graph) {
    commit(LOAD_GRAPH_SUCCESS, graph);
  },
  updateLocalGraph({ commit }, graph: Graph) {
    commit(UPDATE_GRAPH);
    commit(UPDATE_GRAPH_SUCCESS, graph);
  },

  async loadAllGraphData({ commit }, params: any) {
    const response = await allData(
      params.graph,
      params.periode as string[],
      params.haveTab,
    );
    commit(LOAD_GRAPH_SUCCESS, {
      ...params.graph,
      graphVariables: params.haveTab
        ? params.graph.graphVariables.map((variable: any, index: number) => ({
            ...variable,
            data: response.data.objects[index].data.map((elm: any) => {
              return {
                ...elm,
                type: elm.period,
              };
            }),
          }))
        : params.graph.graphVariables.map((variable: any) => {
            const varIndex = response.data.variables.findIndex(
              (varResponse: any) =>
                varResponse.id === variable.object.id &&
                varResponse.aggregation.id == variable.aggregation.id,
            );
            return {
              ...variable,
              autoSampling: response.data.variables[varIndex].autoSampling,
              data: response.data.variables[varIndex].data,
              sampling: response.data.variables[varIndex].sampling,
              samplingUnit: response.data.variables[varIndex].samplingUnit,
            };
          }),
    });
  },
  loadGraphData({ commit }, params: any) {
    if (params.variable) {
      commit(UPDATE_GRAPH_DATA);
      if (params.tab) {
        httpClient
          .get(`${HOSTS.AWS}/${ENDPOINTS.TABLE_DATA}`, {
            params: {
              variableId: params.variable.object.id,
              aggregationId: params.variable.aggregation.id,
              start: params.periode[0],
              end: params.periode[1],
              limit: 10000,
            },
          })
          .then(
            (response: AxiosResponse<GraphData>) => {
              commit(UPDATE_GRAPH_DATA_SUCCESS, {
                ...params.variable,
                data: response.data.data,
                unit: params.variable.unit.name,
              });
            },
            (error) => {
              commit(UPDATE_GRAPH_DATA_FAILURE, error.message);
            },
          );
      } else {
        httpClient
          .get(`${HOSTS.AWS}/${ENDPOINTS.GRAPH_DATA}`, {
            params: {
              id: params.variable.object.id,
              aggregationId: params.variable.aggregation.id,
              type: params.variable.type,
              preferredSampling: params.samplingState
                ? params.sampling
                : params.variable.preferredSampling,
              preferredSamplingUnit: params.samplingState
                ? params.samplingUnit
                : params.variable.preferredSamplingUnit,
              start: params.periode[0],
              end: params.periode[1],
              limit: 10000,
              timeserie: true,
            },
          })
          .then(
            (response: AxiosResponse<GraphData>) => {
              commit(UPDATE_GRAPH_DATA_SUCCESS, {
                ...params.variable,
                data: response.data.data,
                unit: response.data.unit.name,
                preferredSampling: response.data.sampling,
                preferredSamplingUnit: response.data.samplingUnit,
              });
            },
            (error) => {
              commit(UPDATE_GRAPH_DATA_FAILURE, error.message);
            },
          );
      }
    }
  },
  updateGraph({ commit, dispatch }, graph: Graph) {
    commit(UPDATE_GRAPH);

    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.GRAPH}/${graph.id}`, {
        name: graph.name,
        dashboard: {
          id: graph.dashboard.id,
        },
        frontConfig: { ...graph.frontConfig },
        row: graph.row,
        graphVariables: [
          ...graph.graphVariables.map((variable: any) => {
            return {
              ...variable,
              frontConfig: {
                BulletVariableName:
                  variable.frontConfig?.BulletVariableName || null,
                GaugeVariableName:
                  variable.frontConfig?.GaugeVariableName || null,
                timeSeriesVariableName:
                  variable.frontConfig?.timeSeriesVariableName || null,
                columnVariableName:
                  variable.frontConfig?.columnVariableName || null,
                splineVariableName:
                  variable.frontConfig?.splineVariableName || null,
                areaVariableName:
                  variable.frontConfig?.areaVariableName || null,
                areaSplineVariableName:
                  variable.frontConfig?.areaSplineVariableName || null,
                histogrammeVariableName:
                  variable.frontConfig?.histogrammeVariableName || null,
                scatterPlotVariableName:
                  variable.frontConfig?.scatterPlotVariableName || null,
                heatMapVariableName:
                  variable.frontConfig?.heatMapVariableName || null,
                target: variable.frontConfig?.target || null,
                numberValuesSlices:
                  variable.frontConfig?.numberValuesSlices || 10,
                gauge: variable.frontConfig?.gauge || null,
                heatMap: variable.frontConfig?.heatMap || null,
              },
              object: { id: variable.object.id },
              unit: { id: variable.unit.id },
              aggregation: { id: variable.aggregation.id },
              data: undefined,
              id:
                variable.id && variable.id.indexOf("-local") !== -1
                  ? null
                  : variable.id,
            };
          }),
        ],
      })
      .then(
        (response: any) => {
          const responseGraph = response.data;
          const updatedGraph = {
            ...graph,
            periode: undefined as any,
            graphVariables: [...graph.graphVariables].map(
              (elm: any, index: number) => {
                return { ...elm, id: responseGraph.graphVariables[index].id };
              },
            ),
          };

          commit(UPDATE_GRAPH_SUCCESS, updatedGraph);
          if (graph.updateData && graph.periode) {
            dispatch(
              "dataExplorer/loadAllGraphData",
              {
                graph: {
                  ...updatedGraph,
                },
                haveTab:
                  updatedGraph.frontConfig.type === TABLEAU_TYPE ||
                  updatedGraph.frontConfig.type === BAR_PLOT_TYPE ||
                  updatedGraph.frontConfig.type === BULLET ||
                  updatedGraph.frontConfig.type === GAUGE,
                periode: graph.periode,
              },
              {
                root: true,
              },
            );
          }
        },
        (error) => {
          commit(UPDATE_GRAPH_FAILURE, error.message);
        },
      );
  },
  loadReportGraphData({ commit }, graph: any) {
    commit(ADD_REPORT_GRAPH);
    Promise.all(
      graph.variables.map((variable: any) => {
        return httpClient.get(
          `${HOSTS.AWS}/${
            graph.haveTab ? ENDPOINTS.TABLE_DATA : ENDPOINTS.GRAPH_DATA
          }`,
          {
            params: graph.haveTab
              ? {
                  variableId: variable.object.id,
                  start: graph.periode[0],
                  end: graph.periode[1],
                  aggregationId: variable.aggregation.id,
                }
              : {
                  id: variable.object.id,
                  aggregationId: variable.aggregation.id,
                  type: variable.type,
                  preferredSampling: graph.sampling,
                  preferredSamplingUnit: graph.samplingUnit,
                  start: graph.periode[0],
                  end: graph.periode[1],
                  limit: 10000,
                  timeserie: true,
                },
          },
        );
      }),
    ).then(
      (response: any) => {
        commit(ADD_REPORT_GRAPH_SUCCESS, {
          ...graph,
          periode: undefined,
          haveTab: undefined,
          variables: graph.variables.map((variable: any, index: number) => {
            return {
              ...variable,
              data: response[index].data.data,
            };
          }),
        });
      },
      (error: any) => {
        commit(ADD_REPORT_GRAPH_FAILURE, error.message);
      },
    );
  },
  deleteGraph({ commit }, param: any) {
    httpClient.delete(`${HOSTS.AWS}/${ENDPOINTS.GRAPH}/${param.graphId}`).then(
      () => {
        const dashbo = { ...param.dash };
        if (dashbo.graphs) {
          dashbo.graphs = [
            ...dashbo.graphs.filter((graph: any) => graph.id !== param.graphId),
          ];
        }

        commit(UPDATE_DASHBOARD_SUCCESS, { ...dashbo });
      },
      (error) => {
        commit(UPDATE_GRAPH_FAILURE, error.message);
      },
    );
  },
};

async function allData(graph: any, periode: string[], haveTab: boolean) {
  const haveSampling = graph.frontConfig.graphSampling;
  const sampling = graph.frontConfig.sampling;
  const samplingUnit = graph.frontConfig.samplingUnit;
  return httpClient.get(
    `${HOSTS.AWS}/${haveTab ? ENDPOINTS.ALLTABLEDATA : ENDPOINTS.ALLGRAPHDATA}`,
    {
      params: {
        graphId: graph.id,
        start: periode ? periode[0] : "",
        end: periode ? periode[1] : "",
        sampling: !haveTab && haveSampling ? sampling : undefined,
        samplingUnit: !haveTab && haveSampling ? samplingUnit : undefined,
        timeserie: !haveTab ? true : undefined,
        numberOfPoints: !haveTab ? 1000 : undefined,
      },
    },
  );
}
